/**
 * Auth API client.
 *
 * Cookies carry the session, so every call sends `credentials: 'include'` and nothing here ever
 * touches a token. The raw session token is set by the server as an HttpOnly cookie and is
 * deliberately unreadable from script — which is exactly why an XSS bug cannot exfiltrate it.
 *
 * Errors arrive in the one envelope from docs/engineering/07-api-conventions.md. Client code
 * branches on `code`, never on the message, which is localized and may change.
 */

import { isApiErrorBody, type ErrorCode } from '@pius/contracts';

const BASE = '/api/v1/auth';

/** A failed request, carrying the stable code and any field-level detail. */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly fields: Record<string, string> = {},
    /** Shown to users as a Reference ID so support can find the server-side event. */
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    // The session cookie is the whole authentication mechanism; omitting this silently signs
    // every request out.
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (res.status === 204) return undefined as T;

  const payload: unknown = await res.json().catch(() => undefined);

  if (!res.ok) {
    if (isApiErrorBody(payload)) {
      throw new ApiError(
        payload.error.code,
        payload.error.message,
        payload.error.fields ?? {},
        payload.error.correlation_id,
      );
    }
    // A response that is not our envelope means something upstream failed — a proxy error page, a
    // network interception. Do not surface its body; it is not ours and may be anything.
    throw new ApiError('internal_error', 'Something went wrong. Please try again.');
  }

  return payload as T;
}

export interface CurrentAccount {
  id: string;
  email: string;
  display_name: string;
  email_verified: boolean;
  is_site_administrator: boolean;
}

/**
 * A Capability session, when one is held.
 *
 * Carries the grant's Liturgy and kind and **never the credential** — the raw token is not stored
 * anywhere the server could read it back from, which is what makes that structural.
 */
export interface CurrentCapability {
  liturgy_id: string;
  kind: 'participant' | 'administrator';
}

export interface CurrentIdentity {
  account: CurrentAccount | null;
  capability: CurrentCapability | null;
}

/**
 * Both halves, because anonymous-with-a-Capability is a first-class identity rather than an absence.
 *
 * Two nulls is a legitimate answer, not an error. Returning only the account — as this once did —
 * meant the shell could not tell somebody administering a celebration through a shared link from
 * somebody who was simply signed out, and those deserve different words.
 */
export async function fetchMe(): Promise<CurrentIdentity> {
  const res = await fetch(`${BASE}/me`, { credentials: 'include' });
  if (!res.ok) return { account: null, capability: null };

  const body = (await res.json()) as {
    account?: {
      id: string;
      email: string;
      display_name: string;
      email_verified: boolean;
      is_site_administrator: boolean;
    } | null;
    capability?: CurrentCapability | null;
  };

  return { account: body.account ?? null, capability: body.capability ?? null };
}

export function register(input: {
  email: string;
  password: string;
  display_name: string;
}): Promise<{ account_id: string }> {
  return post('/register', input);
}

export function signIn(input: {
  email: string;
  password: string;
  /** "Keep this device signed in". False yields a cookie that dies with the browser. */
  remember_me?: boolean;
}): Promise<{ account_id: string }> {
  return post('/sign-in', input);
}

export function signOut(): Promise<void> {
  return post('/sign-out', {});
}

/**
 * Always resolves, whatever the address. The server answers identically for known and unknown
 * addresses, and the UI must not undo that by rendering a different outcome.
 */
export function requestPasswordReset(email: string): Promise<{ message: string }> {
  return post('/password-reset', { email });
}

export function completePasswordReset(token: string, password: string): Promise<void> {
  return post('/password-reset/complete', { token, password });
}

export function verifyEmail(token: string): Promise<void> {
  return post('/verify-email', { token });
}
